import { FormEvent, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { formatDate, statementOptionLabel } from "../lib/format";
import type {
  CardStatement,
  MemoriaalDraftLine,
  MemoriaalPostResult,
  MemoriaalPreview,
  MemoriaalSettings,
} from "../types";

type PostState =
  | { phase: "posting"; label: string; lineCount: number; startedAt: number }
  | { phase: "done"; result: MemoriaalPostResult; seconds: number }
  | { phase: "failed"; error: string; seconds: number };

function money(n: number): string {
  return n.toLocaleString("nl-NL", {
    style: "currency",
    currency: "EUR",
  });
}

/** Preview + post one credit card statement into BHG Memoriaal (test DB by default). */
export function MemoriaalPanel({
  statement,
  statements,
  onStatementsChanged,
}: {
  statement: string;
  statements: CardStatement[];
  onStatementsChanged: () => Promise<unknown>;
}) {
  const [settings, setSettings] = useState<MemoriaalSettings | null>(null);
  const [preview, setPreview] = useState<MemoriaalPreview | null>(null);
  const [localPeriod, setLocalPeriod] = useState(statement);
  const [statementDate, setStatementDate] = useState("");
  const current = statements.find((s) => s.key === localPeriod) ?? null;
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [postState, setPostState] = useState<PostState | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const postStatusRef = useRef<HTMLDivElement>(null);
  const posting = postState?.phase === "posting";

  useEffect(() => {
    if (postState?.phase !== "posting") return;
    const { startedAt } = postState;
    setElapsed(0);
    const timer = window.setInterval(
      () => setElapsed(Math.floor((Date.now() - startedAt) / 1000)),
      500,
    );
    const warnOnLeave = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warnOnLeave);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("beforeunload", warnOnLeave);
    };
  }, [postState]);

  useEffect(() => {
    if (postState) postStatusRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [postState?.phase]);

  useEffect(() => {
    if (statement) setLocalPeriod(statement);
  }, [statement]);

  useEffect(() => {
    if (!localPeriod && statements.length) setLocalPeriod(statements[0].key);
  }, [localPeriod, statements]);

  useEffect(() => {
    setPreview(null);
    setPostState((s) => (s?.phase === "posting" ? s : null));
  }, [localPeriod]);

  useEffect(() => {
    setStatementDate(current?.statement_date ?? "");
  }, [current?.key, current?.statement_date]);

  async function saveStatementDate() {
    if (!current || !statementDate) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.request<CardStatement>("/imports/statements/statement-date", {
        method: "POST",
        body: JSON.stringify({ key: current.key, statement_date: statementDate }),
      });
      await onStatementsChanged();
      setMessage(`Statement date set to ${formatDate(statementDate)}.`);
      if (preview) {
        const draft = await api.request<MemoriaalPreview>("/memoriaal/preview", {
          method: "POST",
          body: JSON.stringify({ period: current.key }),
        });
        setPreview(draft);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save statement date");
    } finally {
      setBusy(false);
    }
  }

  async function loadSettings() {
    try {
      const s = await api.request<MemoriaalSettings>("/memoriaal/settings");
      setSettings(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load Memoriaal settings");
    }
  }

  useEffect(() => {
    loadSettings();
  }, []);

  async function saveSettings(e: FormEvent) {
    e.preventDefault();
    if (!settings) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await api.request<MemoriaalSettings>("/memoriaal/settings", {
        method: "PUT",
        body: JSON.stringify({
          journal_id: settings.journal_id,
          target_administration: settings.target_administration,
          contra_account_id: settings.contra_account_id,
          description_template: settings.description_template,
          default_vat_code_id: settings.default_vat_code_id,
          fallback_account_id: settings.fallback_account_id,
          default_cost_centre_id: settings.default_cost_centre_id,
          include_excluded: settings.include_excluded,
          mapping_rules: settings.mapping_rules,
        }),
      });
      setSettings(saved);
      setMessage(
        `Settings saved. Posts go to ${saved.target_administration || "active BHG admin"}.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save settings");
    } finally {
      setBusy(false);
    }
  }

  async function runPreview() {
    if (!localPeriod) {
      setError("Pick a credit card statement first");
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const draft = await api.request<MemoriaalPreview>("/memoriaal/preview", {
        method: "POST",
        body: JSON.stringify({ period: localPeriod }),
      });
      setPreview(draft);
      setMessage(
        `Draft for ${draft.description}: ${money(draft.total_debit)} · ${draft.lines.length} lines → ${draft.administration}`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Preview failed");
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }

  function updateLine(index: number, patch: Partial<MemoriaalDraftLine>) {
    if (!preview) return;
    const lines = preview.lines.map((line, i) =>
      i === index ? { ...line, ...patch } : line,
    );
    const total_debit = lines.reduce((s, l) => s + (l.debit_amount || 0), 0);
    const total_credit = lines.reduce((s, l) => s + (l.credit_amount || 0), 0);
    setPreview({ ...preview, lines, total_debit, total_credit });
  }

  async function postToBhg(force = false) {
    if (!preview) return;
    const label = `${preview.description} → ${preview.administration}`;
    if (
      !confirm(
        `Post ${label} (€ ${preview.total_debit.toFixed(2)}) to Memoriaal journal ${preview.journal_id}?`,
      )
    ) {
      return;
    }
    const startedAt = Date.now();
    const seconds = () => Math.max(1, Math.round((Date.now() - startedAt) / 1000));
    setBusy(true);
    setError(null);
    setMessage(null);
    setPostState({ phase: "posting", label, lineCount: preview.lines.length, startedAt });
    try {
      const result = await api.request<MemoriaalPostResult>("/memoriaal/post", {
        method: "POST",
        body: JSON.stringify({
          period: preview.period,
          lines: preview.lines,
          force,
        }),
      });
      setPostState({ phase: "done", result, seconds: seconds() });
      try {
        const refreshed = await api.request<MemoriaalPreview>("/memoriaal/preview", {
          method: "POST",
          body: JSON.stringify({ period: preview.period }),
        });
        setPreview(refreshed);
      } catch {
        setPreview(null);
      }
    } catch (err) {
      setPostState({
        phase: "failed",
        error: err instanceof Error ? err.message : "Post failed",
        seconds: seconds(),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel stack" style={{ marginTop: "1rem" }}>
      <div
        className="row"
        style={{ justifyContent: "space-between", alignItems: "baseline", gap: "0.5rem" }}
      >
        <h3 style={{ margin: 0, fontSize: "1.05rem" }}>Memoriaal (Boekhoudgemak)</h3>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => setShowSettings((v) => !v)}
        >
          {showSettings ? "Hide settings" : "Settings"}
        </button>
      </div>
      <p className="muted" style={{ margin: 0 }}>
        Build one Memoriaal booking for a credit card statement (like “ING CC Juni”) and
        post it to a Multivers administration. It covers exactly the lines of that ING CSV
        import — Boekhoudgemak bank lines are never included. Every line is dated on the
        statement date from the ING PDF (the CSV does not contain it), which also sets the
        period and month name; each description keeps the original transaction date. Test
        target defaults to <code>MVL76009</code>.
      </p>

      {showSettings && settings && (
        <form className="stack" onSubmit={saveSettings}>
          <div className="field">
            <label htmlFor="mem-admin">Target administration</label>
            <input
              id="mem-admin"
              value={settings.target_administration || ""}
              onChange={(e) =>
                setSettings({ ...settings, target_administration: e.target.value })
              }
              placeholder="MVL76009"
            />
          </div>
          <div className="row" style={{ gap: "0.75rem", flexWrap: "wrap" }}>
            <div className="field" style={{ flex: 1, minWidth: "8rem" }}>
              <label htmlFor="mem-journal">Journal</label>
              <input
                id="mem-journal"
                value={settings.journal_id}
                onChange={(e) =>
                  setSettings({ ...settings, journal_id: e.target.value })
                }
              />
            </div>
            <div className="field" style={{ flex: 1, minWidth: "8rem" }}>
              <label htmlFor="mem-contra">Contra (credit) account</label>
              <input
                id="mem-contra"
                value={settings.contra_account_id || ""}
                onChange={(e) =>
                  setSettings({ ...settings, contra_account_id: e.target.value })
                }
                placeholder="e.g. 2700"
                required
              />
            </div>
            <div className="field" style={{ flex: 1, minWidth: "8rem" }}>
              <label htmlFor="mem-fallback">Fallback account</label>
              <input
                id="mem-fallback"
                value={settings.fallback_account_id}
                onChange={(e) =>
                  setSettings({ ...settings, fallback_account_id: e.target.value })
                }
              />
            </div>
            <div className="field" style={{ flex: 1, minWidth: "8rem" }}>
              <label htmlFor="mem-vat">Default VAT code</label>
              <input
                id="mem-vat"
                type="number"
                value={settings.default_vat_code_id}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    default_vat_code_id: Number(e.target.value),
                  })
                }
              />
            </div>
            <div className="field" style={{ flex: 1, minWidth: "8rem" }}>
              <label htmlFor="mem-cc">Cost centre (if required)</label>
              <input
                id="mem-cc"
                value={settings.default_cost_centre_id || ""}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    default_cost_centre_id: e.target.value,
                  })
                }
                placeholder="e.g. 0600"
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="mem-desc">Description template</label>
            <input
              id="mem-desc"
              value={settings.description_template}
              onChange={(e) =>
                setSettings({ ...settings, description_template: e.target.value })
              }
            />
            <span className="muted" style={{ fontSize: "0.8rem" }}>
              Placeholders: {"{month_nl}"}, {"{month}"}, {"{year}"}, {"{period}"}
            </span>
          </div>
          <label className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={settings.include_excluded}
              onChange={(e) =>
                setSettings({ ...settings, include_excluded: e.target.checked })
              }
            />
            <span>Include “no receipt” lines as fallback account</span>
          </label>
          <button className="btn btn-primary" type="submit" disabled={busy}>
            Save Memoriaal settings
          </button>
        </form>
      )}

      <div className="row" style={{ gap: "0.75rem", flexWrap: "wrap", alignItems: "end" }}>
        <div className="field" style={{ flex: 1, minWidth: "10rem" }}>
          <label htmlFor="mem-period">Credit card statement</label>
          <select
            id="mem-period"
            value={localPeriod}
            disabled={posting}
            onChange={(e) => setLocalPeriod(e.target.value)}
          >
            {statements.map((s) => (
              <option key={s.key} value={s.key}>
                {statementOptionLabel(s)}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ minWidth: "10rem" }}>
          <label htmlFor="mem-statement-date">Statement date (ING PDF)</label>
          <div className="row" style={{ gap: "0.4rem" }}>
            <input
              id="mem-statement-date"
              type="date"
              value={statementDate}
              min={current?.start}
              onChange={(e) => setStatementDate(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={
                busy || !statementDate || statementDate === (current?.statement_date ?? "")
              }
              onClick={saveStatementDate}
            >
              Save
            </button>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy || !localPeriod}
          onClick={runPreview}
        >
          {busy ? "Working…" : "Preview draft"}
        </button>
        {preview && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={
              busy ||
              !preview.statement_date ||
              !!preview.already_posted ||
              preview.total_debit <= 0 ||
              preview.total_debit !== preview.total_credit
            }
            onClick={() => postToBhg(false)}
          >
            {posting ? "Posting…" : "Post to Memoriaal"}
          </button>
        )}
        {preview?.already_posted && preview.statement_date && (
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={() => postToBhg(true)}
          >
            Force new post
          </button>
        )}
      </div>

      {postState && (
        <div ref={postStatusRef} role="status" aria-live="polite">
          {postState.phase === "posting" && (
            <div className="post-status">
              <strong>Posting {postState.label}…</strong>
              <div className="progress-bar indeterminate" aria-hidden="true">
                <span />
              </div>
              <span className="muted">
                Sending {postState.lineCount} lines to Boekhoudgemak · {elapsed}s. This
                usually takes under a minute — keep this page open.
              </span>
            </div>
          )}
          {postState.phase === "done" && (
            <div className="post-status post-status-ok">
              <strong>
                ✓ Done — posted to Memoriaal {postState.result.journal_id}/
                {postState.result.journal_transaction}
              </strong>
              <span>
                {postState.result.description} · {postState.result.administration} ·{" "}
                {postState.result.line_count} lines ·{" "}
                {money(Number(postState.result.total_debit))} · period{" "}
                {postState.result.period_number}/{postState.result.fiscal_year}
              </span>
              <span className="muted">
                Took {postState.seconds}s. Check the booking in Boekhoudgemak under journal{" "}
                {postState.result.journal_id}, transaction{" "}
                {postState.result.journal_transaction}.
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ alignSelf: "flex-start" }}
                onClick={() => setPostState(null)}
              >
                Dismiss
              </button>
            </div>
          )}
          {postState.phase === "failed" && (
            <div className="post-status post-status-failed">
              <strong>Posting failed after {postState.seconds}s</strong>
              <span>{postState.error}</span>
              <span className="muted">
                If the error mentions a timeout or network problem, check Boekhoudgemak before
                retrying — the booking may have gone through.
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ alignSelf: "flex-start" }}
                onClick={() => setPostState(null)}
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}

      {preview && (
        <div className="stack" style={{ gap: "0.5rem" }}>
          <p style={{ margin: 0 }}>
            <strong>{preview.description}</strong> · {preview.administration} · journal{" "}
            {preview.journal_id} · {preview.transaction_date}
          </p>
          {preview.statement_start && preview.statement_end && (
            <p className="muted" style={{ margin: 0 }}>
              Statement {preview.statement_label} ·{" "}
              {formatDate(preview.statement_start)} → {formatDate(preview.statement_end)}
            </p>
          )}
          <p className="muted" style={{ margin: 0 }}>
            Debit {money(preview.total_debit)} · Credit {money(preview.total_credit)}
            {preview.unmatched_count
              ? ` · ${preview.unmatched_count} still open in app`
              : ""}
          </p>
          {preview.warnings.length > 0 && (
            <ul className="muted" style={{ margin: 0, paddingLeft: "1.2rem" }}>
              {preview.warnings.slice(0, 8).map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
          <div style={{ overflowX: "auto" }}>
            <table className="data-table" style={{ width: "100%", fontSize: "0.85rem" }}>
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Omschrijving</th>
                  <th>Debet</th>
                  <th>Credit</th>
                  <th>BTW</th>
                </tr>
              </thead>
              <tbody>
                {preview.lines.map((line, i) => (
                  <tr key={`${line.transaction_id ?? "c"}-${i}`}>
                    <td>
                      <input
                        value={line.account_id}
                        onChange={(e) => updateLine(i, { account_id: e.target.value })}
                        style={{ width: "4.5rem" }}
                      />
                    </td>
                    <td>
                      <input
                        value={line.description}
                        onChange={(e) => updateLine(i, { description: e.target.value })}
                        style={{ minWidth: "12rem", width: "100%" }}
                      />
                      {line.warning && (
                        <div className="muted" style={{ fontSize: "0.75rem" }}>
                          {line.warning}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {line.debit_amount ? money(line.debit_amount) : ""}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {line.credit_amount ? money(line.credit_amount) : ""}
                    </td>
                    <td>
                      {line.debit_amount > 0 ? (
                        <input
                          type="number"
                          value={line.vat_code_id ?? ""}
                          onChange={(e) =>
                            updateLine(i, {
                              vat_code_id: e.target.value
                                ? Number(e.target.value)
                                : null,
                            })
                          }
                          style={{ width: "3.5rem" }}
                        />
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {error && <div className="alert">{error}</div>}
      {message && <div className="alert alert-ok">{message}</div>}
    </section>
  );
}
